"use client";

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import Link from "next/link";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "danger-soft";
export type ButtonSize = "sm" | "md" | "lg" | "icon" | "icon-sm";

const base =
  "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl font-semibold transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out active:translate-y-px disabled:pointer-events-none disabled:opacity-50 focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-brand";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-brand text-on-brand shadow-1 hover:bg-brand-strong",
  secondary: "bg-brand-soft text-brand-ink hover:bg-[color-mix(in_oklab,var(--brand-soft)_80%,var(--brand))]",
  outline: "border border-line-strong bg-surface text-ink hover:bg-surface-2",
  ghost: "text-ink-2 hover:bg-surface-2 hover:text-ink",
  danger: "bg-danger text-white hover:brightness-95",
  "danger-soft": "bg-danger-soft text-danger-ink hover:bg-[color-mix(in_oklab,var(--danger-soft)_80%,var(--danger))]",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-10 min-h-10 px-3.5 text-sm",
  md: "h-11 px-4 text-sm",
  lg: "h-12 px-5 text-base",
  icon: "h-11 w-11 p-0",
  "icon-sm": "h-9 w-9 p-0",
};

export function buttonClasses(opts: { variant?: ButtonVariant; size?: ButtonSize; className?: string } = {}) {
  return cn(base, variants[opts.variant ?? "primary"], sizes[opts.size ?? "md"], opts.className);
}

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Icon; hidden while loading. Leading by default (logical start), or trailing with `iconPosition="end"`. */
  icon?: ReactNode;
  iconPosition?: "start" | "end";
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant = "primary", size = "md", loading = false, icon, iconPosition = "start", children, disabled, type = "button", ...rest },
  ref,
) {
  const ic = loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden /> : icon;
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses({ variant, size, className })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {iconPosition === "start" ? ic : null}
      {children}
      {iconPosition === "end" ? ic : null}
    </button>
  );
});

type LinkProps = {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  icon?: ReactNode;
  iconPosition?: "start" | "end";
  children?: ReactNode;
  target?: string;
  rel?: string;
  onClick?: () => void;
  "aria-label"?: string;
  /**
   * Real browser navigation. Use this for public CTAs. A client-side <Link>
   * can swallow the tap after the phone restores the page from the back button.
   */
  native?: boolean;
};

export function ButtonLink({ href, variant = "primary", size = "md", className, icon, iconPosition = "start", children, native, ...rest }: LinkProps) {
  const body = (
    <>
      {iconPosition === "start" ? icon : null}
      {children}
      {iconPosition === "end" ? icon : null}
    </>
  );
  const classes = buttonClasses({ variant, size, className });
  if (native) {
    return (
      <a href={href} className={classes} {...rest}>
        {body}
      </a>
    );
  }
  return (
    <Link href={href} className={classes} {...rest}>
      {body}
    </Link>
  );
}
